import { productPriceCurrencyPositionEnum } from 'src/interfaces';

const baseFontStyleProperties = {
  font: { type: 'string' },
  color: { type: 'string' },
  fontSize: { type: 'string' },
  lineHeight: { type: 'string' },
  letterSpacing: { type: 'string' },
  enable: { type: 'boolean' },
};

const DiscountSchema = {
  type: 'object',
  properties: {
    dealUsed: {
      type: 'object',
      properties: {
        label: { type: 'string' },
      },
      required: ['label'],
    },
    reward: {
      type: 'object',
      properties: {
        label: { type: 'string' },
      },
      required: ['label'],
    },
  },
  required: ['dealUsed'],
};

const TotalConfigSchema = {
  type: 'object',
  properties: {
    background: { type: 'string' },
    total: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
        label: { type: 'string' },
      },
    },
    save: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
        label: { type: 'string' },
      },
    },
    tax: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
        label: { type: 'string' },
      },
    },
    title: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
        label: { type: 'string' },
      },
    },
    donate: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
        label: { type: 'string' },
      },
    },
  },
  required: ['total', 'save', 'tax'],
};

const TopCustomMessageSchema = {
  type: 'object',
  properties: {
    message: { type: 'string' },
    color: { type: 'string' },
    background: { type: 'string' },
  },
};

const TopCustomMessageRequiredSchema = {
  ...TopCustomMessageSchema,
  required: ['message'],
};

const BodyStyleConfigSchema = {
  type: 'object',
  properties: {
    product: {
      type: 'object',
      properties: baseFontStyleProperties,
      required: [],
    },
    subProduct: {
      type: 'object',
      properties: baseFontStyleProperties,
      required: [],
    },
    discounts: {
      type: 'object',
      properties: {
        label: {
          type: 'object',
          properties: {
            ...baseFontStyleProperties,
            backgroundColor: { type: 'string' },
            border: { type: 'string' },
          },
          required: [],
        },
        price: {
          type: 'object',
          properties: {
            ...baseFontStyleProperties,
            border: { type: 'string' },
          },
          required: [],
        },
      },
    },
  },
  additionalProperties: false,
};

const FooterMessageSchema = {
  type: 'object',
  properties: {
    enabledTypeKey: { type: 'string' },
    top: { type: 'number' },
    left: { type: 'number' },
    image: { type: 'string' },
    zIndex: { type: 'number' },
    iconAndText: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
        icon: {
          type: 'string',
        },
        label: {
          type: 'string',
        },
      },
    },
    wrapperStyle: {
      type: 'object',
      properties: {
        background: {
          type: 'string',
        },
        border: {
          type: 'string',
        },
        borderRadius: {
          type: 'string',
        },
        padding: {
          type: 'string',
        },
      },
    },
  },
  required: ['enabledTypeKey'],
  additionalProperties: false,
};

const FooterConfigSchema = {
  type: 'object',
  properties: {
    background: { type: 'string' },
    unknownCustomer: {
      type: 'array',
      items: FooterMessageSchema,
    },
    knownCustomer: {
      type: 'object',
      properties: {
        linkedPayment: {
          type: 'array',
          items: FooterMessageSchema,
        },
        unlinkedPayment: {
          type: 'array',
          items: FooterMessageSchema,
        },
      },
      required: ['linkedPayment', 'unlinkedPayment'],
      additionalProperties: false,
    },
  },
  required: ['unknownCustomer', 'knownCustomer'],
  additionalProperties: false,
};

const ConfirmationHeaderSchema = {
  type: 'object',
  properties: {
    review: {
      type: 'object',
      properties: {
        banner: {
          type: 'object',
          properties: {
            images: {
              type: 'object',
              properties: {
                largeImage: {
                  type: 'object',
                  properties: {
                    background: { type: 'string' },
                  },
                  required: ['background'],
                },
                smallImage: {
                  type: 'object',
                  properties: {
                    background: { type: 'string' },
                  },
                  required: ['background'],
                },
              },
            },
            font: { type: 'string' },
            color: { type: 'string' },
            showBannerImage: { type: 'boolean' },
            backgroundColor: { type: 'string' },
            alignment: { type: 'string' },
            top: { type: 'string' },
            left: { type: 'string' },
            label: { type: 'string' },
          },
          required: ['showBannerImage', 'images'],
        },
        label: { type: 'string' },
      },
      required: ['label', 'banner'],
    },
  },
  required: ['review'],
};

const ConfirmationBodySchema = {
  type: 'object',
  properties: {
    background: { type: 'string' },
    font: { type: 'string' },
    color: { type: 'string' },
    discount: DiscountSchema,
    styleConfig: BodyStyleConfigSchema,
  },
  required: ['styleConfig'],
};

const ConfirmationSchema = {
  type: 'object',
  properties: {
    header: ConfirmationHeaderSchema,
    body: ConfirmationBodySchema,
    total: TotalConfigSchema,
    topMessage: TopCustomMessageSchema,
    footer: FooterConfigSchema,
  },
  required: ['header', 'body', 'footer', 'topMessage'],
  additionalProperties: false,
};
const LmsFeatureFlagsSchema = {
  type: 'object',
  properties: {
    originalPriceCalculation: { type: 'boolean' },
  },
  additionalProperties: false,
};
const GlobalSettingSchema = {
  type: 'object',
  properties: {
    modifierLabels: {
      type: 'object',
      properties: {
        NO: { type: 'string' },
        LIGHT: { type: 'string' },
        ONLY: { type: 'string' },
        LIGHTONLY: { type: 'string' },
      },
    },
    displayVoidedItems: { type: 'boolean' },
    font: { type: 'string' },
    color: { type: 'string' },
    featureFlags: LmsFeatureFlagsSchema,
    background: { type: 'string' },
    productPriceCurrency: { type: 'string' },
    productFullPriceOnly: { type: 'boolean' },
    productPriceCurrencyPosition: {
      enum: [
        productPriceCurrencyPositionEnum.before,
        productPriceCurrencyPositionEnum.after,
      ],
    },
    productPriceDecimalSeparator: { type: 'string' },
  },
  additionalProperties: true,
};
const CodGlobalSettingSchema = {
  ...GlobalSettingSchema,
  properties: {
    ...GlobalSettingSchema.properties,
    confirmationBackground: { type: 'string' },
  },
};

const TopLayoutConfigSchema = {
  type: 'object',
  properties: {
    height: { type: 'string' },
    width: { type: 'string' },
    heightOffset: { type: 'string' },
  },
  additionalProperties: false,
};

const HeaderConfigSchema = {
  type: 'object',
  properties: {
    background: { type: 'string' },
    order: {
      type: 'object',
      properties: {
        font: { type: 'string' },
        color: { type: 'string' },
        label: {
          type: 'string',
        },
      },
      required: ['label'],
    },
    pagination: {
      type: 'object',
      properties: {
        ...baseFontStyleProperties,
      },
      required: [],
    },
  },
  required: ['order', 'pagination'],
  additionalProperties: false,
};

const BodyConfigSchema = {
  type: 'object',
  properties: {
    background: { type: 'string' },
    font: { type: 'string' },
    color: { type: 'string' },
    styleConfig: BodyStyleConfigSchema,
    discount: DiscountSchema,
    orderSelection: {
      type: 'object',
      properties: {
        backgroundColor: { type: 'string' },
        color: { type: 'string' },
      },
      required: ['backgroundColor', 'color'],
    },
  },
  required: ['styleConfig'],
};

const OrderSchema = {
  type: 'object',
  properties: {
    header: HeaderConfigSchema,
    topMessage: TopCustomMessageRequiredSchema,
    layoutConfig: TopLayoutConfigSchema,
    total: TotalConfigSchema,
    body: BodyConfigSchema,
    footer: FooterConfigSchema,
  },
  required: ['header', 'body', 'footer', 'total', 'topMessage'],
};

const CodSettingsSchema = {
  type: 'object',
  properties: {
    globalSetting: CodGlobalSettingSchema,
    codSetting: OrderSchema,
    confirmSetting: ConfirmationSchema,
  },
  required: ['globalSetting', 'codSetting', 'confirmSetting'],
};

export const CodSchema = {
  type: 'object',
  properties: {
    codSettings: CodSettingsSchema,
  },
};
